// Ticket 31: a stopwatch for the craving path, typed -> embedded -> saved ->
// queried -> rendered.
//
// Until this existed the waiting limits on that path (7 s for the embed, 4 s
// and 8 s for search) were reasoned, never measured. This records when each
// step starts and ends so a real run can be read off, and so ticket 42 can
// tune the limits against numbers instead of guesses.
//
// OFF by default, and invisible to readers. It switches on with ?timing=1 on
// the page address (?timing=0 switches it off again) and then stays on for the
// rest of that browser tab. While it is off, every function here returns at
// once and touches nothing.
//
// It only watches. It never changes what a step does, never waits on anything,
// never writes to the database, and every function swallows its own errors, so
// it cannot be the reason a craving fails. What it prints goes to the browser
// console, and each mark is also left in the DevTools Performance timeline as
// "reanlo:crave:<name>".
//
// What it cannot see: time spent inside the edge functions and inside Postgres
// is part of the "embed" and "match" steps here, but not split out. Those two
// numbers are the full wait the reader feels, which is the number the limits
// have to be compared against.

const FLAG_KEY = 'reanlo-timing';

// The marks, in the order a typed craving reaches them. The report names a
// step by the pair of marks around it.
export const CRAVING_MARKS = [
  'submit',          // the reader pressed the button; the stopwatch starts here
  'session-ready',   // login session read
  'embed-start',     // embed-craving called
  'embed-end',       // embed-craving answered, or gave up
  'record-start',    // the cravings row is being saved
  'record-end',      // that save has returned
  'match-start',     // the match step began (before the saved list is read)
  'exclusions-ready',// the saved "don't show me" list has been read
  'query-start',     // match_books called
  'match-end',       // match_books answered
  'books-rendered',  // the cards are on screen, after the browser has painted
  'pipelines-start', // match_pipelines called
  'pipelines-end',   // the pipeline strip has what it needs
];

// [label, from-mark, to-mark] -- the steps the report adds up.
const STEPS = [
  ['session', 'submit', 'session-ready'],
  ['embed', 'embed-start', 'embed-end'],
  ['save', 'record-start', 'record-end'],
  ['exclusions', 'match-start', 'exclusions-ready'],
  ['match', 'query-start', 'match-end'],
  ['paint', 'match-end', 'books-rendered'],
  ['pipelines', 'pipelines-start', 'pipelines-end'],
];

export function timingEnabled() {
  try {
    if (typeof window === 'undefined') return false;
    const flag = new URLSearchParams(window.location.search).get('timing');
    if (flag === '1') {
      try { window.sessionStorage.setItem(FLAG_KEY, '1'); } catch { /* storage blocked: still on for this load */ }
      return true;
    }
    if (flag === '0') {
      try { window.sessionStorage.removeItem(FLAG_KEY); } catch { /* nothing to clear */ }
      return false;
    }
    return window.sessionStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

// Pure, so it can be tested without a browser. marks: [{ name, at, note? }],
// `at` in milliseconds on any one clock. Returns the table rows, the named
// step durations, and the total from the first mark to the last.
export function summariseMarks(marks) {
  const list = Array.isArray(marks) ? marks : [];
  const first = list.length ? list[0].at : 0;
  let prev = first;
  const rows = list.map(m => {
    const row = {
      step: m.name,
      at_ms: Math.round(m.at - first),
      since_previous_ms: Math.round(m.at - prev),
      note: m.note || '',
    };
    prev = m.at;
    return row;
  });
  const atOf = name => {
    const hit = list.find(m => m.name === name);
    return hit ? hit.at : null;
  };
  const steps = {};
  for (const [label, from, to] of STEPS) {
    const a = atOf(from);
    const b = atOf(to);
    if (a != null && b != null) steps[label] = Math.round(b - a);
  }
  const total = list.length ? Math.round(list[list.length - 1].at - first) : 0;
  return { rows, steps, total };
}

let run = null;

function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

// Begin a fresh run, replacing any earlier one. kind: 'typed' for a new
// craving, 'retry' for "Try again", 'filter' for re-running with a filter.
// For anything but 'typed' the first mark is 'match-start' territory, so the
// report simply has fewer steps; nothing is invented for steps that did not run.
export function cravingTimingStart(kind = 'typed') {
  try {
    if (!timingEnabled()) { run = null; return; }
    run = { id: (run?.id ?? 0) + 1, kind, marks: [], seen: new Set() };
    cravingMark('submit');
  } catch { run = null; }
}

export function cravingMark(name, note) {
  try {
    if (!run) return;
    const at = now();
    run.marks.push({ name, at, note });
    run.seen.add(name);
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(`reanlo:crave:${name}`);
    }
    // The run is complete once the cards are painted and the pipeline strip
    // has settled; whichever lands last prints the report.
    if (run.seen.has('books-rendered') && run.seen.has('pipelines-end')) report('complete');
  } catch { /* never let the stopwatch break a craving */ }
}

// For marks that mean "after the browser has drawn it": waits two animation
// frames. Ignored if a newer run has started in the meantime.
export function cravingMarkAfterPaint(name, note) {
  try {
    if (!run) return;
    const id = run.id;
    const fire = () => { if (run && run.id === id) cravingMark(name, note); };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(fire));
    } else {
      fire();
    }
  } catch { /* see cravingMark */ }
}

// End the run early because it failed or was abandoned (embed gave up, the
// match errored). Prints what was seen so far.
export function cravingTimingEnd(reason) {
  try {
    if (!run) return;
    report(reason || 'ended');
  } catch { run = null; }
}

function report(reason) {
  const finished = run;
  run = null;
  if (!finished || finished.marks.length === 0) return;
  const { rows, steps, total } = summariseMarks(finished.marks);
  const parts = Object.entries(steps).map(([k, v]) => `${k} ${v}`).join(', ');
  // eslint-disable-next-line no-console
  console.log(`[crave timing] ${finished.kind} (${reason}): ${total} ms in all${parts ? ` -- ${parts}` : ''}`);
  // eslint-disable-next-line no-console
  console.table(rows);
}
